// SMS passwordless login — two endpoints that map 1:1 to the backend spec.
//
// Flow: requestSmsCode → user receives a 4-digit SMS code →
//   verifySmsCode (user types 4-digit code) → JWT returned, identical
//   shape to the email/Apple/Google responses so AuthContext.login works
//   without branching.

import { apiFetch, ApiError } from "./apiClient";
import { StoredUser } from "../utils/authStorage";

const SMS_AUTH_PATH = "/v1/auth/sms";

export interface SmsAuthResult {
  token: string;
  user: StoredUser;
}

interface SmsVerifyResponse {
  access_token: string;
  name: string | null;
  email: string | null;
}

// Backend currently only supports +86 + 11 digits (starting with 1).
// Validated client-side so we can surface a clean error instead of a 422.
const CN_PHONE_RE = /^1\d{10}$/;

/**
 * Normalize a country code + national-format number into the E.164 form
 * the backend expects (e.g. "+86" + "13812345678" → "+8613812345678").
 * Strips spaces, dashes, parentheses; leaves a leading "+" in the result.
 */
export const formatE164 = (countryCode: string, nationalNumber: string): string => {
  const digits = nationalNumber.replace(/[^\d]/g, "");
  return `${countryCode}${digits}`;
};

/**
 * Returns true iff the national-format number is a valid CN mobile (11 digits
 * starting with 1). The backend rejects everything else with a 422.
 */
export const isValidCnMobile = (nationalNumber: string): boolean => {
  return CN_PHONE_RE.test(nationalNumber.replace(/[^\d]/g, ""));
};

/**
 * POST /sms/request — sends a 4-digit code via SMS.
 * Always returns 204 on success OR silent rate-limit; non-204 means the
 * request was malformed (422) and apiFetch will throw ApiError.
 */
export const requestSmsCode = async (phoneNumber: string): Promise<void> => {
  await apiFetch<void>(`${SMS_AUTH_PATH}/request`, {
    method: "POST",
    body: { phone_number: phoneNumber, app: "capcam" },
  });
};

/**
 * POST /sms/verify-code — exchanges 4-digit code for JWT.
 * Throws ApiError on 401 (bad/expired), 422 (malformed), 429 (locked).
 */
export const verifySmsCode = async (
  phoneNumber: string,
  code: string
): Promise<SmsAuthResult> => {
  const res = await apiFetch<SmsVerifyResponse>(
    `${SMS_AUTH_PATH}/verify-code`,
    {
      method: "POST",
      body: { phone_number: phoneNumber, app: "capcam", code },
    }
  );
  if (!res?.access_token) {
    throw new Error("Auth backend did not return an access_token");
  }
  return {
    token: res.access_token,
    user: {
      name: res.name ?? "",
      email: res.email ?? "",
    },
  };
};

/**
 * Maps ApiError status codes to user-facing i18n keys for the SMS flow.
 */
export const getSmsErrorKey = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 429) return "smsCodeLocked";
    if (err.status === 401) return "smsCodeInvalid";
    if (err.status === 422) return "smsCodeMalformed";
  }
  return "loginNetworkError";
};
